import ViewSettingsControl from '@components/FilterV3/components/ColumnsVisibility/ViewSettingsControl';
import VirtualizedTable from '@components/ListV2/components/table/VirtualizedTable';
import ObjectScreen from '@components/v2/layout/ObjectScreen/ObjectScreen';
import EmptyState from '@components/v2/ui/EmptyState/EmptyState';
import Loading from '@components/v2/ui/Loading/Loading';
import { Box, Stack } from '@mui/material';
import LangEnum from '../../../../../../lang/LangEnum';
import { ColumnsPreferenceRecognizedLists } from '../../../../../../services/modules/userContextPreferences/userContextPreferences.types';
import { OrderObjectTargetsTestIds } from '../../../../../../utils/testIds/OrderObject.testIds';
import TargetErrorState from '../../../../../target/components/TargetErrorState';
import useOrderObjectTargetsTab from './hooks/useOrderObjectTargetsTab';
import { EMPTY_BLOCK_WIDTH } from './orderObjectTargetsTab.constants';
import { OrderTargetRow } from './orderObjectTargetsTab.types';

interface OrderObjectTargetsTabProps {
  orderId: string;
}

const noPaginationToLoad = () => undefined;

const OrderObjectTargetsTab = ({ orderId }: OrderObjectTargetsTabProps) => {
  const {
    rows,
    columns,
    viewConfigs,
    scrollParent,
    isLoading,
    isError,
    errorTitle,
    isEmpty,
    refetch,
  } = useOrderObjectTargetsTab({ orderId });

  if (isError) {
    return <TargetErrorState title={errorTitle} onRetry={refetch} />;
  }

  if (isEmpty) {
    return (
      <Stack sx={{ minHeight: '100%', alignItems: 'center', justifyContent: 'center', p: 3 }}>
        <Box sx={{ maxWidth: EMPTY_BLOCK_WIDTH }}>
          <EmptyState
            testId={OrderObjectTargetsTestIds.EMPTY_STATE}
            title={{ text: LangEnum.ORDER_OBJECT_TARGETS_EMPTY_TITLE }}
            description={{ text: LangEnum.ORDER_OBJECT_TARGETS_EMPTY_DESCRIPTION }}
          />
        </Box>
      </Stack>
    );
  }

  if (isLoading || !scrollParent) {
    return (
      <Stack sx={{ height: '100%', alignItems: 'center', justifyContent: 'center' }}>
        <Loading />
      </Stack>
    );
  }

  return (
    <>
      <ObjectScreen.Controls>
        <ViewSettingsControl
          columns={columns}
          list={ColumnsPreferenceRecognizedLists.ORDER_TARGETS}
          configs={viewConfigs}
          testId={OrderObjectTargetsTestIds.VIEW_BUTTON}
        />
      </ObjectScreen.Controls>

      <VirtualizedTable<OrderTargetRow>
        hideColumnsButton
        stickyGroupHeaders
        isFirstLevelTreeHidden
        fixedTopOffset={0}
        data={rows}
        columns={columns}
        columnsPreferenceRecognizedList={ColumnsPreferenceRecognizedLists.ORDER_TARGETS}
        endReached={noPaginationToLoad}
        customScrollParent={scrollParent}
      />
    </>
  );
};

export default OrderObjectTargetsTab;
